

import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { useOnboarding } from "@/providers/OnboardingProvider";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { useSetupPlan } from "@/lib/query/hooks/useSetupPlan";
import { useSetupVisibility } from "@/pages/event/setup/useVisibleSetupSections";
import { isImmersiveChromeRoute } from "@/lib/immersiveRoutes";
import { isProgrammeRoute } from "@/components/POnboarding/OnboardingCoach";
import { useProgrammePanelStore } from "@/lib/onboarding/programme-panel-store";

const RADIUS = 7;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export const OnboardingPill: React.FC = () => {
  const {
    totalCount,
    percentComplete,
    isMinimized,
    isDismissed,
    isAllComplete,
    recentlyCompletedKeys,
    toggleMinimized,
  } = useOnboarding();

  const pathname = useLocation().pathname;
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const t = useTranslations("onboarding");

  // Event setup journey also keeps the pill alive (even when the company
  // checklist is done/dismissed). Cap-safe read; only fires inside an event.
  const caps = useCapabilitiesOptional();
  // ANCHORED — see POnboarding: `/user/subscription/event/:id` renders under
  // the DashboardShell, where there is no event scope (and no capabilities).
  const eventId = pathname?.match(/^\/event\/([^/]+)/)?.[1] ?? null;
  const setupPlanQuery = useSetupPlan(
    eventId && caps.can("event-setup:read") ? eventId : null,
  );
  // SAME rule the panel uses (module gate + order): counting sections the panel
  // filters away would nag about a journey that then refuses to open.
  const setupProgress = useSetupVisibility(eventId, setupPlanQuery.data);
  const setupTotal = setupProgress.total;
  // Skipped counts as handled for the ring — only still-open sections nag.
  const setupDone = setupTotal - setupProgress.open.length;
  const setupPending = setupProgress.open.length > 0;

  const companyContent = totalCount > 0 && !isDismissed;

  const isImmersiveRoute = isImmersiveChromeRoute(pathname);

  // Programme routes default-minimize the floating panel (it would squat on
  // the timeline canvas — see POnboarding). The pill is the expand affordance
  // there, so it also shows while the panel is only ROUTE-minimized (provider
  // isMinimized false).
  const programmeExpanded = useProgrammePanelStore((s) => s.userExpanded);
  const setProgrammeExpanded = useProgrammePanelStore(
    (s) => s.setUserExpanded,
  );
  const routeMinimized = isProgrammeRoute(pathname) && !programmeExpanded;

  if (
    !hydrated ||
    (!isMinimized && !routeMinimized) ||
    (!companyContent && !setupPending) ||
    isImmersiveRoute
  )
    return null;

  // When only the setup journey is active the ring reflects setup progress.
  const pct = companyContent
    ? percentComplete
    : setupTotal > 0
      ? Math.round((setupDone / setupTotal) * 100)
      : 0;
  const ringComplete = companyContent ? isAllComplete : setupTotal > 0 && setupDone === setupTotal;
  const dashOffset = CIRCUMFERENCE * (1 - pct / 100);
  const hasFreshCompletion = recentlyCompletedKeys.size > 0;

  return (
    <motion.button
      type="button"
      data-testid="onboarding-pill"
      onClick={() => {
        // Expanding: record the explicit programme-route expand (wins over
        // the route default) and clear a persisted minimize if one is set.
        setProgrammeExpanded(true);
        if (isMinimized) toggleMinimized();
      }}
      animate={
        hasFreshCompletion
          ? {
              boxShadow: [
                "0 0 0 0 rgba(80, 168, 102, 0)",
                "0 0 0 5px rgba(80, 168, 102, 0.25)",
                "0 0 0 0 rgba(80, 168, 102, 0)",
              ],
            }
          : { boxShadow: "0 0 0 0 rgba(80, 168, 102, 0)" }
      }
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="flex items-center gap-2 ml-3 px-3 py-1.5 rounded-full border outline-quaternary surface-primary cursor-pointer hover:surface-tertiary transition-colors shrink-0"
    >
      <span className="hidden sm:inline body-3-medium font-primary whitespace-nowrap">
        {t("setupGuide")}
      </span>
      <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0">
        <circle
          cx="9"
          cy="9"
          r={RADIUS}
          fill="none"
          stroke="var(--color-outline-quaternary)"
          strokeWidth="2"
        />
        <motion.circle
          cx="9"
          cy="9"
          r={RADIUS}
          fill="none"
          strokeWidth="2"
          strokeDasharray={CIRCUMFERENCE}
          initial={false}
          animate={{
            strokeDashoffset: dashOffset,
            stroke: ringComplete ? "var(--color-font-success)" : "var(--color-font-blue)",
          }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          strokeLinecap="round"
          transform="rotate(-90 9 9)"
        />
      </svg>
    </motion.button>
  );
};
